// src/pages/etc/mailAlert/MailCheckRunsPanel.jsx
// 조회 이력 패널 — "조회가 돌고 있나"를 회차 단위로 보여준다.
//
// ⚠️ 이 패널의 존재 이유: 설정 카드의 "마지막 조회 성공"은 **1회분**만 보여줘서,
// 조회가 어제부터 멈춰 있어도 화면상으론 한 줄만 바뀐다. 실제로 스케줄 등록이
// 빠져 DB 백업이 이틀 안 돈 일이 있었고(2026-08-24~26), 같은 일이 메일 조회에
// 생기면 알아챌 방법이 없었다. 회차가 쌓여야 "돌고 있다"가 보인다.
//
// 표시 규칙은 서버(views.mail_check_runs)가 적용한다 — 14일이면 약 2,000회차라
// 전부 받아서 프론트에서 거르면 폴링마다 그 양을 실어 나른다.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../../api";

const CARD_STYLE = {
  background: "#fff", border: "1px solid #e9ecef", borderRadius: 10,
  padding: 16, marginTop: 16,
};
const TITLE_STYLE = {
  margin: 0, fontSize: 16, fontWeight: 700, whiteSpace: "nowrap",
  display: "flex", alignItems: "center", gap: 8,
};
// 접기 버튼 — 전역 button{width:100%}에 안 잡히도록 크기를 직접 준다.
const TOGGLE_BTN_STYLE = {
  width: "auto", height: 28, flexShrink: 0, whiteSpace: "nowrap",
  border: "1px solid #ced4da", borderRadius: 6, background: "#fff",
  color: "#495057", fontSize: 12, cursor: "pointer", padding: "0 10px",
};
const ROW_STYLE = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "6px 8px", borderBottom: "1px solid #f1f3f5",
  fontSize: 13, whiteSpace: "nowrap",
};
const TIME_STYLE = { width: 104, flexShrink: 0, color: "#868e96", fontVariantNumeric: "tabular-nums" };
const BADGE = (color, bg) => ({
  flexShrink: 0, padding: "1px 6px", borderRadius: 10, fontSize: 11,
  fontWeight: 700, color, background: bg, whiteSpace: "nowrap",
});
// 요약 문구 — 표가 좁아져도 꺾지 않고 말줄임으로 처리한다(프로젝트 nowrap 규칙).
const TEXT_STYLE = { flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" };

const fmtTime = (iso) => {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd} ${hh}:${mi}`;
};

// 회차 1건 → 한 줄 요약.
// ⚠️ null인 값은 아예 문장에서 뺀다. 로그 복원분은 조회 건수·발송 수가 로그에
// 없어서 모르는 값인데, 0으로 적으면 화면이 "0건 검사"·"0명 발송"이라고 단언하게 된다.
function summarize(r) {
  // 실제 회차가 아니라 "그날 09시대가 비어 있다"는 합성 경고 줄
  if (r.reason === "missing_anchor") return "09시 조회 없음 — 그 시간대에 조회가 돌지 않았습니다";
  if (r.error) return "조회 실패";
  if (r.matched_count) {
    const kws = [...new Set((r.logs || []).map((l) => l.matched_keyword))].filter(Boolean);
    const head = kws.length ? kws.join(", ") : "매칭";
    const tail = r.notified_count != null ? ` → 알림 발송 ${r.notified_count}명` : "";
    return `${head} ${r.matched_count}건${tail}`;
  }
  // checked_count는 "커서 이후 새로 들어온 메일 수"다. 0은 검사를 안 했다는 뜻이
  // 아니라 새 메일이 없었다는 뜻이라, 그대로 "0건 검사"라고 쓰면 오해를 준다.
  if (r.checked_count == null) return "이상 없음";
  return r.checked_count === 0 ? "이상 없음 (새 메일 없음)" : `이상 없음 (새 메일 ${r.checked_count}건 검사)`;
}

export default function MailCheckRunsPanel({ refreshKey = 0, onOpenInbox }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/mail-alert/runs/", { params: { days: 14 } });
      setData(res.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // refreshKey — "지금 확인"이 끝나면 부모가 올려서 즉시 다시 불러오게 한다.
  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = data?.rows || [];

  return (
    <div style={CARD_STYLE}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: open ? 12 : 0 }}>
        <h3 style={TITLE_STYLE}>
          조회 이력
          <span style={{ fontSize: 12, fontWeight: 400, color: "#868e96" }}>최근 14일</span>
        </h3>
        <button style={TOGGLE_BTN_STYLE} onClick={() => setOpen((v) => !v)}>
          {open ? "접기 ▲" : "펼치기 ▼"}
        </button>
      </div>

      {open && (
        <>
          {loading && !data && <div style={{ fontSize: 13, color: "#868e96" }}>불러오는 중…</div>}
          {error && <div style={{ fontSize: 13, color: "#be123c", whiteSpace: "nowrap" }}>이력을 불러오지 못했습니다: {error}</div>}

          {data && rows.length === 0 && (
            <div style={{ fontSize: 13, color: "#868e96" }}>표시할 회차가 없습니다.</div>
          )}

          {rows.map((r) => {
            const isMissing = r.reason === "missing_anchor";
            const isErr = !!r.error;
            const clickable = !isErr && r.matched_count > 0;
            return (
              <div
                key={r.id}
                style={{
                  ...ROW_STYLE,
                  background: isErr ? "#fff5f5" : undefined,
                  cursor: clickable ? "pointer" : "default",
                }}
                onClick={clickable ? () => onOpenInbox?.() : undefined}
                title={isErr ? r.error : (clickable ? "수신 알림함에서 보기" : undefined)}
              >
                <span style={TIME_STYLE}>{fmtTime(r.started_at)}</span>

                {/* 합성 경고 줄은 "실패"가 아니라 "누락"이다 — 조회가 실패한 게 아니라
                    아예 돌지 않았다는 뜻이라 라벨을 구분한다. */}
                {isMissing && <span style={BADGE("#be123c", "#ffe3e3")}>누락</span>}
                {!isMissing && isErr && <span style={BADGE("#be123c", "#ffe3e3")}>실패</span>}
                {r.trigger === "manual" && <span style={BADGE("#1971c2", "#e7f5ff")}>수동</span>}
                {/* 로그에서 되살린 회차 — 트리거를 알 수 없고 일부 수치가 비어 있다 */}
                {r.is_restored && <span style={BADGE("#5f3dc4", "#f3f0ff")}>로그 복원분</span>}

                <span style={{ ...TEXT_STYLE, color: isErr ? "#c92a2a" : "#343a40", fontWeight: isErr ? 600 : 400 }}>
                  {summarize(r)}
                  {isErr && !isMissing && <span style={{ fontWeight: 400, color: "#e03131" }}> — {r.error}</span>}
                </span>

                {clickable && <span style={{ flexShrink: 0, color: "#4a90e2", fontSize: 12 }}>보기 →</span>}
              </div>
            );
          })}

          {/* ⚠️ 숨긴 회차 수는 반드시 보여준다 — 개수를 빼면 "조회가 안 돌았다"와
              "돌았는데 조용했다"가 화면에서 똑같아 보이는데, 이 패널이 있는 이유가
              정확히 그 둘을 구분하는 것이다. */}
          {data && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#868e96", whiteSpace: "nowrap" }}>
              그 외 정상 조회 {data.hidden_ok_count}회는 접어 뒀습니다 · 기간 내 전체 {data.total_runs}회
              {data.truncated && " · 표시 상한(300행)에 걸려 일부만 나옵니다"}
            </div>
          )}
        </>
      )}
    </div>
  );
}
